import * as React from 'react';
import styled from '@emotion/styled';
import { SiteRoute } from '@journeyapps/package-types';
import { CardGridWidget } from '../widgets/CardGridWidget';
import { CardWidget } from '../widgets/CardWidget';
import { ContentWidget } from '../widgets/ContentWidget';
import { PageWidget } from '../widgets/layout/PageWidget';
import { TaskConnectionWidget } from '../widgets/TaskConnectionWidget';

export const NotFoundPage: React.FC = () => (
  <PageWidget title="404 — Page not found" width={1080}>
    <S.Header>
      <S.Connection viewBox="0 0 560 150" aria-hidden="true">
        <S.Node x="10" y="35" width="150" height="80" rx="18" />
        <S.NodeLabel x="85" y="69">
          Your request
        </S.NodeLabel>
        <S.Symbol x="85" y="97">
          GET /
        </S.Symbol>
        <path d="M160 75 H245" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.2" />
        <TaskConnectionWidget d="M160 75 H245" />
        <path d="M270 67 l8 16 m-1 -16 l8 16" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M308 75 H395" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="5 7" opacity="0.35" />
        <S.MissingNode x="395" y="20" width="150" height="110" rx="18" />
        <S.Question x="470" y="75">
          ?
        </S.Question>
        <S.NodeLabel x="470" y="108">
          Page not found
        </S.NodeLabel>
      </S.Connection>
      <S.Title>Not found</S.Title>
      <S.Whoops>Whoops, that’s a 404!</S.Whoops>
      <S.Message>A platform for almost anything. Except this page, apparently.</S.Message>
    </S.Header>
    <CardGridWidget>
      <CardWidget
        title="Back to the platform."
        description="Explore what you can build with JourneyApps."
        href={SiteRoute.HOMEPAGE}
        action="Go home"
        featured
      />
      <CardWidget
        title="Find your next step."
        description="Tutorials, guides, and references to keep you building."
        href={SiteRoute.DOCS}
        action="Explore the docs"
      />
      <CardWidget
        title="See what’s new."
        description="The latest releases, product updates, and ideas."
        href={SiteRoute.BLOG}
        action="Read the blog"
      />
    </CardGridWidget>
  </PageWidget>
);

namespace S {
  const HEADING_GAP = 48;

  export const Connection = styled.svg`
    display: block;
    width: min(100%, 560px);
    margin: 0 auto;
    overflow: visible;
    color: ${(p) => p.theme.home.walkthroughButton.border};
  `;

  export const Node = styled.rect`
    fill: ${(p) => p.theme.surface.background};
    stroke: currentColor;
    stroke-width: 1.5;
  `;

  export const MissingNode = styled(Node)`
    color: ${(p) => p.theme.notFound.accent};
    stroke-dasharray: 5 7;
  `;

  export const NodeLabel = styled.text`
    fill: ${(p) => p.theme.text.paragraph};
    font-size: 14px;
    text-anchor: middle;
  `;

  export const Symbol = styled.text`
    fill: currentColor;
    font: 14px monospace;
    text-anchor: middle;
  `;

  export const Question = styled.text`
    fill: ${(p) => p.theme.notFound.accent};
    font-size: 44px;
    font-weight: 650;
    text-anchor: middle;
  `;

  export const Title = styled.h1`
    && {
      margin: ${HEADING_GAP}px 0;
      font-size: clamp(64px, 12vw, 144px);
      font-weight: 750;
      letter-spacing: normal;
      line-height: 1;
    }
  `;

  export const Header = styled(ContentWidget)`
    text-align: center;
    margin-bottom: 40px;
  `;

  export const Message = styled.p`
    && {
      margin: 4px 0;
    }
  `;

  export const Whoops = styled(Message)`
    && {
      font-size: 20px;
    }
  `;
}
